import React from "react";
import { Avatar, Tooltip } from "@fluentui/react-components";
import HoverCard from "../../components/hoverCard/HoverCard";
import {
    formatMonthYear,
    getReshuffleStatusLabel,
    getReshuffleStatusColorClass,
    formatPeriodMonthYear,
} from "../../helpers/utils";
import styles from "./ReshuffleRequest.module.scss";
import { getAccountCurrency, displayDecimalFormatted } from "../../utils/systemSettings";
import { TrendingDown, TrendingUp } from "lucide-react";
import SafeHtml from "../../components/safeHtml/SafeHtml";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";

const setting = JSON.parse(localStorage.getItem("settings"));

const formatCurrency = (value) => {
    return `${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(value))}`;
};

const formatNumber = (value) => {
    return displayDecimalFormatted(setting, Number(value));
};

const DeltaValue = ({ value, isBill = false }) => {
    if (!isBill ? value > 0 : value < 0) return <span className={styles.deltaNegative}>{`${formatCurrency(value)}`}</span>;
    if (!isBill ? value < 0 : value > 0) return <span className={styles.deltaPositive}>{`${formatCurrency(value)}`}</span>;
    return <span className={styles.deltaNeutral}>{formatCurrency(0)}</span>;
};

const UserHoverCell = ({ user }) => {
    if (!user) return <span></span>;
    return (
        <HoverCard
            HoverCardTrigger={
                <div className={styles.userCell}>
                    <Avatar
                        name={user.name}
                        image={{ src: user.image }}
                        color="colorful"
                        size={20}
                    />
                    <span>{user.name}</span>
                </div>
            }
            AvatarId={user.id}
            AvatarName={user.name}
            AvatarImage={user.image}
            AvatarDesignation={user.title}
            AvatarEmail={user.email}
            AvatarPhone={user.phoneNumber}
            AvatarIsExternal={user.isExternal}
        />
    );
};

const FinancialsCard = ({ title, data }) => {
    if (!data) return null;
    const fields = [
        // { label: "BAC", key: "bac" },
        // { label: "Budget", key: "budget" },
        // { label: "ETC", key: "etc" },
        // { label: "Actual", key: "actual" },
        // { label: "Accrual", key: "accural" },
        // { label: "Spent", key: "spent" },
        // { label: "Invoiced", key: "invoiced" },
        // { label: "CV", key: "cv" },
        // { label: "YTD Forecasted Hours", key: "forecastHours" },
        { label: "YTD Forecasted Cost", key: "forecastCost" },
        { label: "YTD Forecasted Bill", key: "forecastBill" },
    ];
    return (
        <div className={styles.financialsCard}>
            <div className={styles.financialsCardTitle}>{title}</div>
            {fields.map(({ label, key }) => (
                <div key={key} className={styles.financialRow}>
                    <span>{label}</span>
                    <span>{formatCurrency(data[key])}</span>
                </div>
            ))}
        </div>
    );
};

const renderDeltaPill = (delta, isBill) => {
    const threshold = 0.01;

    let className = styles.neutralBadge;
    let text = `${isBill ? "Bill" : "Cost"} `;
    let icon = <></>;

    if (delta > threshold) {
        className = styles.increaseBadge;
        icon = <TrendingUp size={12} className="mr-1" />;
        text += `Increase • ${`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Math.abs(delta))}`}`;
    } else if (delta < -threshold) {
        className = styles.decreaseBadge;
        icon = <TrendingDown size={12} className="mr-1" />;
        text += `Decrease • ${`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Math.abs(delta))}`}`;
    }
    else {
        text += "Neutral";
    }

    return <span className={`${className} ${styles.summaryDeltaBadge} `}>{icon}{text}</span>;
};

/**
 * ReshuffleSummaryPreview
 * Accepts `preview` (ReshuffleSummaryDto) and renders the full summary layout.
 * Used in Step 3 of the create wizard and in the "Summary" view mode.
 */
const ReshuffleSummaryPreview = ({ preview }) => {
    if (!preview) return null;

    const { request, resourceBreakdown, projectFinancialsBefore, projectFinancialsAfter, totalCostDelta, totalBillDelta } = preview;
    const deltaCost = totalCostDelta > 0 ? "positive" : "negative";
    const deltaBill = totalBillDelta > 0 ? "positive" : "negative";

    return (
        <div className={styles.summaryWrapper}>
            {/* ── Header ──────────────────────────────────────────────────────── */}
            {request && (
                <div className={styles.summaryHeader}>
                    <div className={styles.summaryCell}>
                        <p className={styles.summaryTitle}>
                            <UserHoverCell user={request.targetResource} />
                        </p>
                    </div>
                    <div className={styles.summaryCell}>
                        <p className={styles.summarySubtitle}>
                            <span className={styles.summaryStatusBadge}>
                                {`${formatMonthYear(request.year, request.month)} • ${formatNumber(request.requestedHours)} hrs`}
                            </span>
                        </p>
                    </div>
                    <div className={styles.summaryCell}>
                        {renderDeltaPill(totalCostDelta, false)}
                    </div>
                    <div className={styles.summaryCell}>
                        {renderDeltaPill(totalBillDelta, true)}
                    </div>
                    <div className={styles.summaryCell}>
                        {request.status !== undefined && (
                            <span className={styles.summaryStatusBadge}>
                                {getReshuffleStatusLabel(request.status)}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* ── Cost Delta ──────────────────────────────────────────────────── */}
            {/* <div className={styles.costDeltaSection}>
                <div className={`${styles.costDeltaCard} ${styles[deltaCost]}`}>
                    <div className={styles.costDeltaLabel}>Total Cost Delta</div>
                    <div className={`${styles.costDeltaValue} ${styles[deltaCost]}`}>
                        {totalCostDelta > 0 ? "+" : ""}{formatCurrency(totalCostDelta)}
                    </div>
                </div>
                <div className={`${styles.costDeltaCard} ${styles[deltaBill]}`}>
                    <div className={styles.costDeltaLabel}>Total Bill Delta</div>
                    <div className={`${styles.costDeltaValue} ${styles[deltaBill]}`}>
                        {totalBillDelta > 0 ? "+" : ""}{formatCurrency(totalBillDelta)}
                    </div>
                </div>
            </div> */}

            {/* ── Resource Breakdown ──────────────────────────────────────────── */}
            {resourceBreakdown?.length > 0 && (
                <>
                    <div className={styles.sectionTitle}>Request Breakdown</div>
                    <table className={styles.breakdownTable}>
                        <thead>
                            <tr>
                                <th>Role</th>
                                <th>Resource</th>
                                <th>Month</th>
                                <th>Hours</th>
                                <Tooltip content={"Hours Variance"} relationship="label" positioning="above-start" withArrow>
                                    <th>HV</th>
                                </Tooltip>
                                <th>Cost Rate</th>
                                <th>Cost</th>
                                <Tooltip content={"Cost Variance"} relationship="label" positioning="above-start" withArrow>
                                    <th>CV</th>
                                </Tooltip>
                                <th>Bill Rate</th>
                                <th>Bill</th>
                                <Tooltip content={"Bill Variance"} relationship="label" positioning="above-start" withArrow>
                                    <th>BV</th>
                                </Tooltip>
                            </tr>
                        </thead>
                        <tbody>
                            {resourceBreakdown.map((row, i) => {
                                const hrsDelta = (row.projectedForecastHours || 0) - (row.currentForecastHours || 0);
                                return (
                                    <tr key={i}>
                                        <td>
                                            {row.role === "Target" || row.role === 0 ? (
                                                <span className={styles.roleBadgeTarget}>Target</span>
                                            ) : (
                                                <span className={styles.roleBadgeDonor}>Donor</span>
                                            )}
                                        </td>
                                        <td><UserHoverCell user={row.resource} /></td>
                                        <td>{formatMonthYear(row.year, row.month)}</td>
                                        <td>{`${formatNumber(row.currentForecastHours)} -> ${formatNumber(row.projectedForecastHours)} `}</td>
                                        <td>
                                            {hrsDelta > 0 ? (
                                                <span className={styles.deltaPositive}>+{formatNumber(hrsDelta)}</span>
                                            ) : hrsDelta < 0 ? (
                                                <span className={styles.deltaNegative}>{formatNumber(hrsDelta)}</span>
                                            ) : (
                                                <span className={styles.deltaNeutral}>0</span>
                                            )}
                                        </td>
                                        <td>{formatCurrency(row.hourlyCostRate)}</td>
                                        <td>{`${formatCurrency(row.currentForecastCost)} -> ${formatCurrency(row.projectedForecastCost)} `}</td>
                                        <td><DeltaValue value={row.costDelta} /></td>
                                        <td>{formatCurrency(row.hourlyBillRate)}</td>
                                        <td>{`${formatCurrency(row.currentForecastBill)} -> ${formatCurrency(row.projectedForecastBill)} `}</td>
                                        <td><DeltaValue value={row.billDelta} isBill /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}

            {/* ── Project Financials Before / After ──────────────────────────── */}
            {(projectFinancialsBefore || projectFinancialsAfter) && (
                <>
                    <div className={styles.sectionTitle}>Project Financials</div>
                    <div className={styles.financialsGrid}>
                        <FinancialsCard title="Before" data={projectFinancialsBefore} />
                        <FinancialsCard title="After" data={projectFinancialsAfter} />
                    </div>
                </>
            )}

            {request && request.notes && (
                <>
                    <div className={styles.sectionTitle}>Notes</div>
                    <div className={styles.summaryNotes}>
                        <SafeHtml
                            html={request.notes}
                        />
                    </div>
                </>

            )}
            {request && request.reviewNotes && (
                <>
                    <div className={styles.sectionTitle}>Review Notes</div>
                    <div className={styles.summaryNotes}>
                        <SafeHtml
                            html={request.reviewNotes}
                        />
                    </div>
                </>
            )}
            {request && request.attachments && request.attachments.length > 0 && (
                <>
                    <div className={styles.sectionTitle}>Attachments</div>
                    <div className={styles.summaryNotes}>
                        <EntityFileUpload
                            label=""
                            entityId={request.id}
                            entityType="ReshuffleRequest"
                            attachments={request.attachments}
                            onAttachmentsChange={() => { }}
                            disabled={true}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default ReshuffleSummaryPreview;
